import React from 'react';
import { Trans } from 'react-i18next';
import DateTimeSlotPicker from 'components/date-time-slot-picker';
import { setHours, setMinutes, getDay } from 'date-fns';

const timezoneLabels = {
  'US/Alaska': 'Alaska Time',
  'US/Aleutian': 'Aleutian Time',
  'US/Arizona': 'Arizona Time',
  'US/Central': 'Central Time',
  'US/East-Indiana': 'Eastern Time (Indiana)',
  'US/Eastern': 'Eastern Time',
  'US/Hawaii': 'Hawaii Time',
  'US/Indiana-Starke': 'Central Time (Indiana)',
  'US/Michigan': 'Eastern Time (Michigan)',
  'US/Mountain': 'Mountain Time',
  'US/Pacific': 'Pacific Time',
  'US/Pacific-New': 'Pacific Time',
  'US/Samoa': 'Samoa Time'
};

const currentDate = new Date();
export const getToursSettingsInfo = (location = {}) => {
  const calendar = location.aptlyListings?.overrideCalendarEnabled
    ? location.aptlyListings?.overrideCalendar
    : location.aptlyListings?.defaultCalendar;

  const tourType = location?.aptlyListings?.overrideTourMode
    ? location?.aptlyListings?.tourType
    : location?.companyInfo?.aptlyListings?.tourType;

  const calendarMode = location.aptlyListings?.overrideCalendarMode
    ? location.aptlyListings.calendarMode
    : location?.companyInfo?.aptlyListings?.calendarMode;

  const isTimePickerVisible =
    ['self-tour', 'resident'].includes(tourType) && ['aptly'].includes(calendarMode);

  return { tourType, calendar, calendarMode, isTimePickerVisible };
};

const getTimezoneLabel = (timezone) => {
  if (!timezone) return null;
  return timezoneLabels[timezone] || timezone;
};

const tourTypes = { selfTour: 'self-tour', agentLead: 'agent-lead', resident: 'resident' };

const TourDatePicker = ({ location, value, onChange }) => {
  if (!location) return null;
  const { calendar, tourType } = getToursSettingsInfo(location);
  const multiTimeRanges = calendar?.multiTimeRanges;
  const tourDurationMin = calendar?.tourDurationMin;
  const calendarTimezone = calendar?.businessHours?.timezone;

  const isValidTime = (time, day) => {
    if (!calendar) return true;

    const dayConfig = multiTimeRanges?.[day];
    if (!dayConfig || !dayConfig.active) return false;

    const timeHour = time.getHours();
    const timeMinute = time.getMinutes();
    const timeInMinutes = timeHour * 100 + timeMinute;

    return (
      dayConfig.slots?.some((slot) => {
        return timeInMinutes >= slot.startTime && timeInMinutes < slot.endTime;
      }) || false
    );
  };

  const filterTime = (time) => {
    const selectedDay = getDay(time);
    const currentDate = new Date();

    if (tourType === tourTypes.resident) {
      const twentyFourHoursFromNow = new Date(currentDate.getTime() + 24 * 60 * 60 * 1000);
      if (time < twentyFourHoursFromNow) {
        return false;
      }
    } else if (time < currentDate) {
      return false;
    }

    return isValidTime(time, selectedDay);
  };

  const filterDate = (date) => {
    if (!calendar) return true;

    const dayOfWeek = getDay(date);
    const currentDate = new Date();
    currentDate.setHours(0, 0, 0, 0);
    const checkDate = new Date(date);
    checkDate.setHours(0, 0, 0, 0);

    if (checkDate < currentDate) return false;

    return multiTimeRanges?.[dayOfWeek]?.active || false;
  };

  const handleDateChange = (date) => {
    let adjustedDate = date;
    let foundValidTime = filterTime(date);

    if (!foundValidTime) {
      const dayOfWeek = getDay(date);
      const dayConfig = multiTimeRanges?.[dayOfWeek];
      if (dayConfig?.active && dayConfig.slots?.length > 0) {
        const interval = tourDurationMin || 60;

        for (const slot of dayConfig.slots) {
          const startHour = Math.floor(slot.startTime / 100);
          const startMinutes = slot.startTime % 100;
          const endHour = Math.floor(slot.endTime / 100);
          const endMinutes = slot.endTime % 100;

          let candidateTime = setMinutes(setHours(new Date(date), startHour), startMinutes);
          const endTime = setMinutes(setHours(new Date(date), endHour), endMinutes);

          let iterations = 0;
          const maxIterations = 100;
          while (candidateTime <= endTime && iterations < maxIterations) {
            if (filterTime(candidateTime)) {
              adjustedDate = candidateTime;
              foundValidTime = true;
              break;
            }
            candidateTime = new Date(candidateTime.getTime() + interval * 60 * 1000);
            iterations++;
          }

          if (foundValidTime) break;
        }
      }
    }

    onChange(foundValidTime ? adjustedDate : '');
  };

  return (
    <DateTimeSlotPicker
      selected={value}
      onChange={handleDateChange}
      filterDate={filterDate}
      filterTime={filterTime}
      timeIntervals={tourDurationMin || 60}
      minDate={new Date()}
      maxDaysOut={5}
      timezoneLabel={
        calendarTimezone && (
          <Trans i18nKey="showing.detail.tour.timezoneLabel">
            Times shown in {{ timezone: getTimezoneLabel(calendarTimezone) }}
          </Trans>
        )
      }
    />
  );
};

export default TourDatePicker;
